
import React, { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Attendee } from '@/types/attendee';
import { SearchInput, SearchDialog, FloatingSearchButton } from '@/components/admin/search';
import { cn } from '@/lib/utils';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  placeholder?: string;
  sticky?: boolean;
  floatingOnMobile?: boolean;
  attendees?: Attendee[];
  onAttendeeSelect?: (attendee: any) => void;
  lastSearches?: string[];
  onRefreshData?: () => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  placeholder = "Tìm kiếm tên, email, SĐT, mã số...",
  sticky = true,
  floatingOnMobile = false,
  attendees = [],
  onAttendeeSelect,
  lastSearches = [],
  onRefreshData
}) => {
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showQuickResults, setShowQuickResults] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handle input focus
  const handleInputFocus = () => {
    if (attendees.length > 0) {
      setShowQuickResults(true);
    }
  };

  // Handle search input change
  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    if (value.length >= 2 && attendees.length > 0) {
      setShowQuickResults(true);
    } else {
      setShowQuickResults(false);
    }
  };

  // Handle input clear
  const handleClear = () => {
    setSearchQuery('');
    setShowQuickResults(false);
  };

  // Handle attendee selection
  const handleAttendeeClick = (attendee: any) => {
    if (onAttendeeSelect) {
      onAttendeeSelect(attendee);
    }

    // Add haptic feedback when selecting
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
    setShowQuickResults(false);
    setSearchQuery('');
    setIsDialogOpen(false);
  };

  // Handle silent data refresh
  const handleSilentRefresh = () => {
    if (onRefreshData && !isRefreshing) {
      setIsRefreshing(true);

      // Call the refresh function
      onRefreshData();

      // Reset the refreshing state after a delay
      setTimeout(() => {
        setIsRefreshing(false);

        // Add haptic feedback when refresh completes
        if ('vibrate' in navigator) {
          navigator.vibrate([30, 30, 30]);
        }
      }, 1000);
    }
  };

  // When dialog opens on mobile, remember scroll position
  useEffect(() => {
    if (isDialogOpen && isMobile) {
      const scrollPosition = window.scrollY;
      return () => {
        // Restore scroll position when dialog closes
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 100);
      };
    }
  }, [isDialogOpen, isMobile]);

  if (isMobile && floatingOnMobile) {
    return <>
        <div className="fixed right-4 flex flex-col space-y-3 z-40">
          {onRefreshData && 
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                type: "spring", 
                damping: 15, 
                stiffness: 300 
              }}
            >
              <Button 
                onClick={handleSilentRefresh} 
                size="icon" 
                disabled={isRefreshing} 
                className="rounded-full shadow-lg w-12 h-12 bg-green-500 hover:bg-green-600 text-white fixed top-1/2 right-4 transform -translate-y-1/2"
              >
                <RefreshCw size={18} className={`transition-all ${isRefreshing ? "animate-spin" : ""}`} />
              </Button>
            </motion.div>
          }
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              type: "spring", 
              damping: 15, 
              stiffness: 300,
              delay: 0.1
            }}
          >
            <FloatingSearchButton onClick={() => setIsDialogOpen(true)} />
          </motion.div>
        </div>
        
        <SearchDialog 
          isOpen={isDialogOpen} 
          onOpenChange={setIsDialogOpen} 
          attendees={attendees} 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
          onAttendeeSelect={handleAttendeeClick} 
          recentSearches={lastSearches} 
        />
      </>;
  }

  return <div className={cn('relative flex-1', sticky ? 'sticky top-0 z-20 bg-white dark:bg-gray-800 py-2' : '')}>
      <SearchInput value={searchQuery} onChange={handleInputChange} onFocus={handleInputFocus} onClear={handleClear} placeholder={placeholder} />
      
      {/* Quick results will be handled by the elastic search feature in AttendeesContent */}
    </div>;
};

export default SearchBar;
